import type { StateTree } from '@makemydeal/dr-dash-types';
import { INCENTIVE_TYPE_VALUES } from '@makemydeal/dr-dash-types';
import type { ManualIncentiveEntry } from '@makemydeal/dr-platform-types';
import { utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import type { ManualIncentiveEditEntry } from '../types/manualIncentiveTypes';
import { sortStandardIncentivesFirst, transformToManualEditIncentive } from '../utils/manualIncentivesUtils';
import * as offerReduxSelectors from './offerRedux';

const getRecordedSelectionIds = (state: StateTree): number[] | null => {
    const hasRecorded =
        offerReduxSelectors.hasRecordedConditionalSelection(state) || offerReduxSelectors.hasRecordedNonConditionalSelection(state);
    if (!hasRecorded) {
        return null;
    }
    return [
        ...offerReduxSelectors.getSelectedConditionalIdsFromPenciledInputs(state),
        ...offerReduxSelectors.getSelectedNonConditionalIdsFromPenciledInputs(state)
    ];
};

const isRecalcPending = (state: StateTree): boolean =>
    Boolean((state as StateTree & { baseDeal?: { paymentCallPending?: boolean } }).baseDeal?.paymentCallPending);

const buildPendingDeselectFilter = (state: StateTree): ((incentive: ManualIncentiveEntry) => boolean) => {
    if (!isRecalcPending(state)) {
        return () => true;
    }
    const recordedIds = getRecordedSelectionIds(state);
    if (recordedIds == null) {
        return () => true;
    }
    const recorded = new Set(recordedIds);
    return (incentive: ManualIncentiveEntry) =>
        incentive.manual === true || incentive.code == null || recorded.has(Number(incentive.code));
};

const buildPairableFilter = (state: StateTree): ((incentive: ManualIncentiveEntry) => boolean) => {
    const keepThroughPendingDeselect = buildPendingDeselectFilter(state);
    return (incentive: ManualIncentiveEntry) =>
        (incentive.code != null || Boolean(incentive.name)) && keepThroughPendingDeselect(incentive);
};

export const getIncentiveValidationErrorMessage = (state: StateTree): string => {
    return state.incentiveValidation?.errorMessage || '';
};

export const getHasIncentiveValidationErrors = (state: StateTree): boolean => {
    return state.incentiveValidation?.hasErrors || false;
};

// Same incentive by identity: by code when either side has one (string-normalized so 100 and "100"
// match), otherwise a codeless entry matched by name. Used to pair applied incentives with their raw row.
const isSameManualIncentiveIdentity = (applied: ManualIncentiveEntry, raw: ManualIncentiveEditEntry): boolean => {
    if (applied.code != null || raw.code != null) {
        return String(applied.code) === String(raw.code);
    }
    return Boolean(applied.name) && applied.name === raw.name;
};

const hasSameProvenance = (applied: ManualIncentiveEntry, raw: ManualIncentiveEditEntry): boolean =>
    Boolean(applied.manual) === Boolean(raw.manual);

const isPairableRawRow = (applied: ManualIncentiveEntry, raw: ManualIncentiveEditEntry): boolean =>
    isSameManualIncentiveIdentity(applied, raw) && hasSameProvenance(applied, raw);

const isSameEditId = (applied: ManualIncentiveEntry, raw: ManualIncentiveEditEntry): boolean => {
    const appliedEditId = (applied as ManualIncentiveEditEntry)._editId;
    return appliedEditId != null && appliedEditId === raw._editId;
};

type RawRowMatcher = (applied: ManualIncentiveEntry, raw: ManualIncentiveEditEntry) => boolean;

const claimRawRows = (
    applied: ManualIncentiveEntry[],
    raws: ManualIncentiveEditEntry[],
    matcher: RawRowMatcher,
    rawIndexByApplied: Map<number, number>,
    consumed: Set<number>
): void => {
    applied.forEach((entry, appliedIndex) => {
        if (rawIndexByApplied.has(appliedIndex)) {
            return;
        }
        const rawIndex = raws.findIndex((raw, i) => !consumed.has(i) && matcher(entry, raw));
        if (rawIndex !== -1) {
            rawIndexByApplied.set(appliedIndex, rawIndex);
            consumed.add(rawIndex);
        }
    });
};

const pairRawRowsToApplied = (
    applied: ManualIncentiveEntry[],
    raws: ManualIncentiveEditEntry[],
    restorationEnabled: boolean
): Map<number, number> => {
    const rawIndexByApplied = new Map<number, number>();
    const consumed = new Set<number>();
    claimRawRows(applied, raws, isSameEditId, rawIndexByApplied, consumed);
    claimRawRows(applied, raws, isPairableRawRow, rawIndexByApplied, consumed);
    if (!restorationEnabled) {
        claimRawRows(applied, raws, isSameManualIncentiveIdentity, rawIndexByApplied, consumed);
    }
    return rawIndexByApplied;
};

type DxgShapedRow = ManualIncentiveEditEntry & {
    pdId?: number;
    incentiveType?: ManualIncentiveEditEntry['type'];
    programId?: string;
};

const normalizeRawRow = (raw: DxgShapedRow): ManualIncentiveEditEntry => ({
    ...raw,
    code: raw.code ?? raw.pdId,
    type: raw.type || raw.incentiveType,
    program: raw.program || raw.programId
});

const withDefaultManualType = (incentive: ManualIncentiveEditEntry): ManualIncentiveEditEntry =>
    Boolean(incentive.manual) && !incentive.type ? { ...incentive, type: INCENTIVE_TYPE_VALUES.customerCash.value } : incentive;

export const toShownManualRow = (raw: DxgShapedRow): ManualIncentiveEditEntry => withDefaultManualType(normalizeRawRow(raw));

/**
 * Returns manual incentives preserving _editId from raw Redux state.
 * Built on top of getAppliedIncentives (which normalizes via transformToBaseIncentive)
 * so the property shape matches — only _editId is added from raw data.
 *
 * `_editId` is matched by incentive identity, not by array index: under
 * enableProgramIncentivesRestoration_F292564 getAppliedIncentives returns
 * [...mergedProgram, ...manualOnly], which is a different order/length from the raw
 * manual node, so index alignment would scramble the ids and break row-identity matching
 * in the edit form (wrong row deleted on the trash icon).
 */
export const getAppliedIncentivesWithEditIds = (state: StateTree): ManualIncentiveEditEntry[] => {
    const appliedIncentives = offerReduxSelectors.getAppliedIncentives(state);
    const rawIncentives = ((offerReduxSelectors.getManualIncentives(state) ?? []) as DxgShapedRow[]).map(normalizeRawRow);
    if (!appliedIncentives?.length) return [];

    const restorationEnabled = offerReduxUtils.incentives.isProgramIncentivesRestorationEnabled(state);

    const pairableApplied = restorationEnabled ? appliedIncentives.filter(buildPairableFilter(state)) : appliedIncentives;

    const rawIndexByApplied = pairRawRowsToApplied(pairableApplied, rawIncentives, restorationEnabled);
    const consumedRawIndexes = new Set<number>(rawIndexByApplied.values());

    const withEditIds = pairableApplied.map((inc, appliedIndex) => {
        const rawIndex = rawIndexByApplied.get(appliedIndex);
        const raw = rawIndex == null ? undefined : rawIncentives[rawIndex];
        const entry = { ...transformToManualEditIncentive(inc), _editId: raw?._editId };
        return restorationEnabled && raw?.manual != null ? { ...entry, manual: raw.manual } : entry;
    });

    if (!restorationEnabled) {
        return withEditIds;
    }

    const droppedUserRows = rawIncentives
        .filter((raw, i) => !consumedRawIndexes.has(i) && raw.manual === true && !raw.deleted)
        .map((raw) => ({ ...raw, originalAmount: raw.originalAmount || raw.amount, deleted: false }));

    const merged = [...withEditIds, ...droppedUserRows].map(withDefaultManualType);
    return sortStandardIncentivesFirst(merged);
};
